/**
 * @fileoverview Titled card for a Sales Performance section.
 * @description The white bordered card every section of the Sales Performance screen sits in: a
 * title, an optional subtitle or count line beside it, and the section's content. Keeps the
 * sections' headings and spacing identical.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { ReactNode } from 'react'

interface PerformanceCardProps {
  title: string
  subtitle?: string
  meta?: string
  testId: string
  busy?: boolean
  children: ReactNode
}

/**
 * @component
 * @param {PerformanceCardProps} props - The card's heading and content.
 * @param {string} props.title - The section's heading.
 * @param {string} [props.subtitle] - A line under the heading saying what it shows.
 * @param {string} [props.meta] - A short count line shown at the right of the heading.
 * @param {string} props.testId - `data-testid` of the card.
 * @param {boolean} [props.busy] - Whether what the card shows still belongs to the previous
 * selection; it is dimmed and marked busy until the new one arrives.
 * @param {ReactNode} props.children - The section's content.
 */
function PerformanceCard({
  title,
  subtitle,
  meta,
  testId,
  busy = false,
  children,
}: PerformanceCardProps) {
  return (
    <section
      className={`flex flex-col gap-3.5 rounded-card border border-divider bg-white p-4 transition-opacity sm:p-5 ${busy ? 'opacity-50' : ''}`}
      aria-busy={busy}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-md font-bold text-ink" data-testid={`${testId}-title`}>
            {title}
          </h2>
          {subtitle && (
            <span className="text-xs text-slate" data-testid={`${testId}-subtitle`}>
              {subtitle}
            </span>
          )}
        </div>
        <span className="flex-1" />
        {meta && (
          <span className="text-compact-sm font-medium text-slate" data-testid={`${testId}-meta`}>
            {meta}
          </span>
        )}
      </div>
      {children}
    </section>
  )
}

export default PerformanceCard
